import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "../styles/BlockedSlot.css";

export default function BlockedSlot({ onClose }) {
    const { token } = useAuth();
    const [blockedSlots, setBlockedSlots] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const [form, setForm] = useState({
        startDateTime: "",
        endDateTime: "",
        reason: ""
    });

    useEffect(() => {
        fetchBlockedSlots();
    }, []);

    const fetchBlockedSlots = async () => {
        try {
            const res = await fetch(
                `${API_BASE_URL}/api/auth/solicitor/blocked-slots`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = await res.json();

            if (res.ok) {
                setBlockedSlots(data.blockedSlots || []);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setShowForm(false);
        setForm({
            startDateTime: "",
            endDateTime: "",
            reason: ""
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.startDateTime || !form.endDateTime) {
            toast.error("Please select start and end date");
            return;
        }

        if (form.startDateTime > form.endDateTime) {
            toast.error("From date cannot be after To date");
            return;
        }

        const payload = {
            startDateTime: `${form.startDateTime}T00:00`,
            endDateTime: `${form.endDateTime}T23:59`,
            reason: form.reason
        };

        try {
            const url = editingId
                ? `${API_BASE_URL}/api/auth/solicitor/blocked-slots/${editingId}`
                : `${API_BASE_URL}/api/auth/solicitor/blocked-slots`;

            const method = editingId ? "PUT" : "POST";

            const res = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();

            if (!res.ok) {
                toast.error(data.message || "Failed to save slot");
                return;
            }

            setBlockedSlots(data.blockedSlots || []);
            resetForm();
            toast.success(editingId ? "Slot updated successfully" : "Slot blocked successfully");
        } catch (err) {
            toast.error("Operation failed");
            console.error(err);
        }
    };

    const handleEdit = (slot) => {
        setEditingId(slot._id);
        setShowForm(true);

        setForm({
            startDateTime: slot.startDateTime.slice(0, 10),
            endDateTime: slot.endDateTime.slice(0, 10),
            reason: slot.reason || ""
        });
    };

    const handleDelete = async (slotId) => {

        const result = await Swal.fire({
            title: "Delete blocked slot?",
            text: "This blocked period will be removed permanently.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            reverseButtons: true
        });

        if (!result.isConfirmed) return;

        try {
            const res = await fetch(
                `${API_BASE_URL}/api/auth/solicitor/blocked-slots/${slotId}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = await res.json();

            if (!res.ok) {
                toast.error(data.message || "Delete failed");
                return;
            }

            setBlockedSlots(data.blockedSlots || []);
            toast.success("Slot removed successfully");

        } catch (err) {
            console.error(err);
            toast.error("Delete failed");
        }
    };


    const formatDateRange = (start, end) => {
        const startDate = new Date(start).toLocaleDateString('en-GB');
        const endDate = new Date(end).toLocaleDateString('en-GB');
        return startDate === endDate ? startDate : `${startDate} - ${endDate}`;
    };

    return (
        <div className="blocked-slot-overlay">
            <div className="blocked-slot-card">
                <div className="blocked-slot-header">
                    <h2>Manage Unavailability Slots</h2>
                    <p className="blocked-slot-subtitle">Add dates when you're unavailable for appointments</p>
                </div>

                {/* Existing Blocked Slots */}
                <div className="blocked-slots-section">
                    <div className="section-header">
                        <h3>Blocked Periods</h3>
                        <span className="slot-count">{blockedSlots.length} slot(s)</span>
                    </div>

                    {blockedSlots.length > 0 ? (
                        <div className="blocked-slot-list">
                            {blockedSlots.map(slot => (
                                <div key={slot._id} className="blocked-slot-item">
                                    <div className="slot-info">
                                        <div className="slot-dates">
                                            {formatDateRange(slot.startDateTime, slot.endDateTime)}
                                        </div>
                                        {slot.reason && (
                                            <div className="slot-reason">
                                                <span className="reason-badge">{slot.reason}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="slot-actions">
                                        <button
                                            type="button"
                                            className="btn-edit"
                                            onClick={() => handleEdit(slot)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-delete"
                                            onClick={() => handleDelete(slot._id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>No blocked slots added yet</p>
                        </div>
                    )}

                    {/* Add Slot Button */}
                    {!showForm && (
                        <div className="add-slot-container">
                            <button
                                type="button"
                                className="btn-add-slot"
                                onClick={() => setShowForm(true)}
                            >
                                + Add New Block Slot
                            </button>
                        </div>
                    )}
                </div>

                {/* Slot Form (Conditional) */}
                {showForm && (
                    <form onSubmit={handleSubmit} className="blocked-slot-form">
                        <div className="form-header">
                            <h3>{editingId ? "Edit Blocked Slot" : "Add New Blocked Slot"}</h3>
                        </div>

                        <div className="form-group">
                            <label htmlFor="startDateTime">From Date *</label>
                            <input
                                id="startDateTime"
                                type="date"
                                value={form.startDateTime}
                                onChange={(e) => setForm({ ...form, startDateTime: e.target.value })}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="endDateTime">To Date *</label>
                            <input
                                id="endDateTime"
                                type="date"
                                value={form.endDateTime}
                                onChange={(e) => setForm({ ...form, endDateTime: e.target.value })}
                                required
                                min={form.startDateTime}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="reason">Reason (Optional)</label>
                            <input
                                id="reason"
                                type="text"
                                placeholder="e.g., Vacation, Personal Leave, Holiday"
                                value={form.reason}
                                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                            />
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                className="btn-secondary"
                                onClick={resetForm}
                            >
                                {editingId ? "Cancel Edit" : "Cancel"}
                            </button>
                            <button type="submit" className="btn-primary">
                                {editingId ? "Update Slot" : "Block Slot"}
                            </button>
                        </div>
                    </form>
                )}

                {/* Close Button */}
                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn-close"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}